/**
 * Adds custom inflection rules, such as plural, singular, uninflected, or irregular.
 *
 * @param {string} type - The type of inflection, either 'plural', 'singular', 'uninflected', or 'irregular'.
 * @param {Array} rules - Array of rules to be added.
 */
class Inflector {
	_plural = {
		'(s)tatus$': '$1tatuses',
		'info$': '$1',
		'(quiz)$': '$1zes',
		'^(ox)$': '$1$2en',
		'([m|l])ouse$': '$1ice',
		'(matr|vert)(ix|ex)$': '$1ices',
		'(x|ch|ss|sh)$': '$1es',
		'([^aeiouy]|qu)y$': '$1ies',
		'(hive)$': '$1s',
		'(chef)$': '$1s',
		'(?:([^f])fe|([lre])f)$': '$1$2ves',
		'sis$': 'ses',
		'([ti])um$': '$1a',
		'(p)erson$': '$1eople',
		'(?<!u)(m)an$': '$1en',
		'(c)hild$': '$1hildren',
		'(buffal|tomat)o$': '$1$2oes',
		'(alumn|bacill|cact|foc|fung|nucle|radi|stimul|syllab|termin)us$': '$1i',
		'us$': 'uses',
		'(alias)$': '$1es',
		'(ax|cris|test)is$': '$1es',
		's$': 's',
		'^$': '',
		'$': 's'
	}

	_singular = {
		'(s)tatuses$': '$1$2tatus',
		'status': 'status',
		'history': 'history',
		'^(.*)(menu)s$': '$1$2',
		'(quiz)zes$': '$1',
		'(matr)ices$': '$1ix',
		'(vert|ind)ices$': '$1ex',
		'^(ox)en': '$1',
		'(alias|lens)(es)*$': '$1',
		'(alumn|bacill|cact|foc|fung|nucle|radi|stimul|syllab|termin|viri?)i$': '$1us',
		'([ftw]ax)es': '$1',
		'(cris|ax|test)es$': '$1is',
		'(shoe)s$': '$1',
		'(o)es$': '$1',
		'ouses$': 'ouse',
		'([^a])uses$': '$1us',
		'([m|l])ice$': '$1ouse',
		'(x|ch|ss|sh)es$': '$1',
		'(m)ovies$': '$1$2ovie',
		'(s)eries$': '$1$2eries',
		'(s)pecies$': '$1$2pecies',
		'([^aeiouy]|qu)ies$': '$1y',
		'(tive)s$': '$1',
		'(hive)s$': '$1',
		'(drive)s$': '$1',
		'([le])ves$': '$1f',
		'([^rfoa])ves$': '$1fe',
		'(^analy)ses$': '$1sis',
		'(analy|diagno|^ba|(p)arenthe|(p)rogno|(s)ynop|(t)he)ses$/i': '$1$2sis',
		'([ti])a$': '$1um',
		'(p)eople$': '$1$2erson',
		'(m)en$': '\\1an',
		'(c)hildren$': '$1$2hild',
		'(n)ews$': '$1$2ews',
		'eaus$': 'eau',
		'^(.*us)$': '$1',
		's$': '',
	}

	_irregular = {
		'atlas': 'atlases',
		'beef': 'beefs',
		'brief': 'briefs',
		'brother': 'brothers',
		'cafe': 'cafes',
		'child': 'children',
		'cookie': 'cookies',
		'corpus': 'corpuses',
		'cow': 'cows',
		'criterion': 'criteria',
		'ganglion': 'ganglions',
		'genie': 'genies',
		'genus': 'genera',
		'graffito': 'graffiti',
		'hoof': 'hoofs',
		'loaf': 'loaves',
		'man': 'men',
		'money': 'monies',
		'mongoose': 'mongooses',
		'move': 'moves',
		'mythos': 'mythoi',
		'niche': 'niches',
		'numen': 'numina',
		'occiput': 'occiputs',
		'octopus': 'octopuses',
		'opus': 'opuses',
		'ox': 'oxen',
		'penis': 'penises',
		'person': 'people',
		'sex': 'sexes',
		'soliloquy': 'soliloquies',
		'testis': 'testes',
		'trilby': 'trilbys',
		'turf': 'turfs',
		'potato': 'potatoes',
		'hero': 'heroes',
		'tooth': 'teeth',
		'goose': 'geese',
		'foot': 'feet',
		'foe': 'foes',
		'sieve': 'sieves',
		'cache': 'caches',
	}

	_uninflected = [
		'.*[nrlm]ese', '.*data', '.*deer', '.*fish', '.*measles', '.*ois',
		'.*pox', '.*sheep', 'people', 'feedback', 'stadia', '.*?media',
		'chassis', 'clippers', 'debris', 'diabetes', 'equipment', 'gallows',
		'graffiti', 'headquarters', 'information', 'innings', 'news', 'nexus',
		'pokemon', 'proceedings', 'research', 'sea[- ]bass', 'series', 'species', 'weather',
		'staff', 'media', 'menu', 'info', 'menus', 'store', 'sort', 'order', 'store', 'data', 'meta', 'history', 'sync'
	]

	_initialState = {}

	constructor () {
		this._cache = {
			_uninflected : {},
			_irregular : {},
			_singularize : {},
			_pluralize : {}
		}
	}

	cache (type, key, value = false) {
		key = '_' + key
		type = '_' + type
		this._cache[type] = this._cache[type] || {}
		if (value !== false) {
			this._cache[type][key] = value
			return value
		}
		if (!this._cache[type][key]) {
			return false
		}
		return this._cache[type][key]
	}

	reset () {
		if (Object.keys(this._initialState).length === 0) {
			this._initialState = Object.assign({}, this)
			return
		}
		for (let key in this._initialState) {
			if (key !== '_initialState') {
				this[key] = this._initialState[key]
			}
		}
	}

	/**
	 * Sets the inflection rules for the specified type.
	 *  Adds custom inflection $rules, of either 'plural', 'singular',
	 * 'uninflected' or 'irregular' $type.
	 * Usage:
	 *
	 * Inflector.rules('plural', [{'/^(inflect)or$/i' : '\1ables'}]);
	 * Inflector.rules('irregular', [{'red' => 'redlings'}]);
	 * Inflector.rules('uninflected', ['dontinflectme']);
	 *
	 * @param {{plural,irregular,uninflected}} type - The type for which the rules are being set.
	 * @param {Object|array} rules - The rules to be set for the specified type.
	 * @param {boolean} [reset=false] - Specifies whether to reset the rules or merge them with existing rules.
	 *
	 * @return {undefined}
	 */
	rules (type, rules, reset = false) {
		let varName = '_' + type
		if (reset) {
			this[varName] = rules
		} else if (type === 'uninflected') {
			this._uninflected = [...rules, ...this._uninflected]
		} else {
			this[varName] = { ...rules, ...this[varName] }
		}
		this._cache = {
			_irregular : {},
			_singularize : {},
			_pluralize : {}
		}
	}

	/**
	 * Returns the plural form of a word, if it exists.
	 * If the word is already in plural form or is uninflected, the original word is returned.
	 *
	 * @param {string} word - The word to be pluralized.
	 * @return {string} - The pluralized form of the word, if it exists; otherwise, the original word.
	 */
	pluralize (word) {
		if (this._cache._pluralize && this._cache._pluralize[word]) {
			return this._cache._pluralize[word]
		}

		if (!this._cache._irregular.pluralize) {
			const words = Object.keys(this._irregular)
			this._cache._irregular.pluralize = new RegExp(`(.*?(?:\\b|_))(${words.join('|')})$`, 'i')

			const upperWords = words.map(word => word.charAt(0).toUpperCase() + word.slice(1))
			this._cache._irregular.upperPluralize = new RegExp(`(.*?(?:\\b|[a-z]))(${upperWords.join('|')})$`)
		}

		let match = word.match(this._cache._irregular.pluralize) || word.match(this._cache._irregular.upperPluralize)
		if (match) {
			this._cache._pluralize = this._cache._pluralize || {}
			this._cache._pluralize[word] = match[1] + this._irregular[match[2].toLowerCase()]//.slice(1)
			return this._cache._pluralize[word]
		}

		if (!this._cache._uninflected) {
			this._cache._uninflected = new RegExp(`^(${this._uninflected.join('|')})$`, 'i')
		}

		if (this._cache._uninflected.test(word)) {
			this._cache._pluralize = this._cache._pluralize || {}
			this._cache._pluralize[word] = word
			return word
		}

		for (let rule in this._plural) {
			if (new RegExp(rule, 'i').test(word)) {
				this._cache._pluralize = this._cache._pluralize || {}
				//console.log(rule, this._plural[rule], word, );
				this._cache._pluralize[word] = word.replace(new RegExp(rule, 'i'), this._plural[rule])
				//console.log(rule, this._plural[rule], word, this._cache._pluralize[word] );
				return this._cache._pluralize[word]
			}
		}

		return word
	}

	/**
	 * Singularizes a word.
	 *
	 * @param {string} word - The word to be singularized.
	 * @return {string} - The singular form of the word.
	 */
	singularize (word) {
		if (this._cache._singularize && this._cache._singularize[word]) {
			return this._cache._singularize[word]
		}

		if (!this._cache._irregular.singular) {
			const wordList = Object.values(this._irregular)
			this._cache._irregular.singular = new RegExp(`(.*?(?:\\b|_))(${wordList.join('|')})$`, 'i')

			const upperWordList = wordList.map(word => word.charAt(0).toUpperCase() + word.slice(1))
			this._cache._irregular.singularUpper = new RegExp(`(.*?(?:\\b|[a-z]))(${upperWordList.join('|')})$`)
		}

		let match = word.match(this._cache._irregular.singular) || word.match(this._cache._irregular.singularUpper)
		if (match) {
			this._cache._singularize = this._cache._singularize || {}
			let suffix = this._irregular[match[2].toLowerCase()] ? this._irregular[match[2].toLowerCase()].substring(1) : ''
			this._cache._singularize[word] = match[1] + match[2].charAt(0) + suffix
			return this._cache._singularize[word]
		}

		if (!this._cache._uninflected) {
			this._cache._uninflected = new RegExp(`^(${this._uninflected.join('|')})$`, 'i')
		}

		if (this._cache._uninflected.test(word)) {
			this._cache._singularize = this._cache._singularize || {}
			this._cache._singularize[word] = word
			return word
		}

		let regex = /s$/i
		console.log(word, regex.test(word));
		regex = new RegExp('s$', 'i')
		console.log(word, regex.test(word));
		regex = new RegExp('/s$', 'i')
		console.log(word, regex.test(word));

		for (let rule in this._singular) {
			if (regex.test(word)) {
				this._cache._singularize = this._cache._singularize || {}
				this._cache._singularize[word] = word.replace(new RegExp(rule, 'i'), this._singular[rule])
				return this._cache._singularize[word]
			}
		}

		this._cache._singularize = this._cache._singularize || {}
		this._cache._singularize[word] = word
		return word
	}

	/**
	 * Returns the input lower_case_delimited_string as a CamelCasedString or camelCasedString.
	 *
	 * @param {string} string - The string to be converted to camel case.
	 * @param {string} [delimiter=_] - The delimiter used in the string. Default is underscore (_).
	 * @return {*} - The converted camel case string.
	 */
	camelize (string, delimiter = '_') {

		const cacheKey = 'camelize' + delimiter

		let result = this.cache(cacheKey, string)

		if (result === false) {
			result = this.humanize(string, delimiter).replace(/\s+/g, '')
			this.cache(cacheKey, string, result)
		}

		return result
	}

	/**
	 * Returns the input CamelCasedString as an underscored_string.
	 *
	 * @param {string} string - The input string to perform the replacement on.
	 * @return {boolean|string} - The resulting string with dashes replaced by underscores.
	 */
	underscore (string) {
		return this.delimit(string.replaceAll(/-/g, '_'), '_')
	}

	/**
	 * Returns the input CamelCasedString as an dashed-string.
	 *
	 * @param {string} string - The input string to be dasherized.
	 * @return {string} - The dasherized string.
	 */
	dasherize (string) {
		return this.delimit(string.replaceAll(/_/g, '-'), '-')
	}

	/**
	 * Returns the input lower_case_delimited_string as 'A Human Readable String'. (Underscores are replaced by spaces
	 * and capitalized following words.)
	 *
	 * @param {string} string - The input string to be converted.
	 * @param {string} [delimiter='_'] - The delimiter used to split words.
	 * @return {string} - The converted string in human-readable format.
	 */
	humanize (string, delimiter = '_') {
		const cacheKey = 'humanize' + delimiter

		let result = this.cache(cacheKey, string)

		if (result === false) {
			result = string.replaceAll(delimiter, ' ').split(' ').map(word =>
				word.charAt(0).toUpperCase() + word.slice(1)
			).join(' ')
			this.cache(cacheKey, string, result)
		}

		return result
	}

	/**
	 * Expects a CamelCasedInputString, and produces a lower_case_delimited_string
	 *
	 * @param {string} string - The string to be delimited.
	 * @param {string} [delimiter='_'] - The delimiter to be inserted between words or capital letters. Default is '_'.
	 * @return {string} - The delimited string.
	 */
	delimit (string, delimiter = '_') {
		const cacheKey = 'delimit' + delimiter

		let result = this.cache(cacheKey, string)

		if (result === false) {
			result = string.replaceAll(/(?<=\w)([A-Z])/g, delimiter + '$1').toLowerCase()
			this.cache(cacheKey, string, result)
		}

		return result.replaceAll("__", "_");
	}

	/**
	 * Returns corresponding table name for given model $className. ("people" for the model class "Person").
	 *
	 * @param {string} className - The name of the class.
	 * @return {*} - The table name.
	 */
	tableize (className) {
		let result = this.cache('tableize', className)

		if (result === false) {
			result = this.pluralize(this.underscore(className))
			this.cache('tableize', className, result)
		}

		return result
	}

	/**
	 * Returns ("Person" for the database table "people".) for given database table.
	 *
	 * @param {string} tableName - The name of the table to classify.
	 * @return {string} - The classified name.
	 */
	classify (tableName) {
		let result = this.cache('classify', tableName)

		if (result === false) {
			result = this.camelize(this.singularize(tableName))
			this.cache('classify', tableName, result)
		}

		return result
	}

	/**
	 * Returns camelBacked version of an underscored string.
	 *
	 * @param {string} string - The input string to convert.
	 * @return {*} - The converted string.
	 */
	variable (string) {
		let result = this.cache('variable', string)

		if (result === false) {
			let camelized = this.camelize(this.underscore(string))
			result = camelized.charAt(0).toLowerCase() + camelized.slice(1)
			this.cache('variable', string, result)
		}

		return result
	}
}

export default new Inflector()
